import { Component, PhysXComponent, Object3D } from '@wonderlandengine/api';
import {
    CursorTarget,
    Cursor,
    FingerCursor,
} from '@wonderlandengine/components';

export class InteractableBase extends Component {
    static TypeName = 'interactable-base';

    private _target!: CursorTarget;

    protected _physxObject!: PhysXComponent;

    /**
     * Called when the component starts. Retrieves the CursorTarget component
     * from the object.
     */
    start() {
        this._target = this.object.getComponent(CursorTarget)!; // Assuming the object has a CursorTarget component
    }

    /**
     * Called when the component is activated. Sets up event listeners for
     * cursor interactions and retrieves the PhysX component.
     */
    onActivate(): void {
        this._physxObject = this.object.getComponent(PhysXComponent)!;
        this._target.onHover.add(this._onhover);
        this._target.onUnhover.add(this._onhover);
        this._target.onDown.add(this._ondown);
        this._target.onUp.add(this._onup);
    }
    /**
     * Called when the component is deactivated. Removes event listeners for
     * cursor interactions.
     */
    onDeactivate(): void {
        this._target.onHover.remove(this._onhover);
        this._target.onUnhover.remove(this._onUnhover);
        this._target.onDown.remove(this._ondown);
        this._target.onUp.remove(this._onup);
    }

    /**
     * Called every frame to update the component.
     * @param delta - Time elapsed since the last frame in seconds
     */
    update(delta: number): void {}

    /**
     * Virtual method to be overridden by subclasses. Called when the user
     * interacts with the object.
     * @param cursor - The cursor that triggered the interaction
     * @virtual
     */
    interact(cursor: Cursor) {}

    /**
     * Handles cursor hover events. Filters out FingerCursor events.
     * @param obj - The object being hovered
     * @param c - The cursor or finger cursor that triggered the event
     */
    private _onhover = (obj: Object3D, c: Cursor | FingerCursor) => {
        if (c instanceof FingerCursor) {
            // Ignore, since it's not used
        } else {
            // Handle regular cursor hover
        }
    };

    /**
     * Handles cursor unhover events. Filters out FingerCursor events.
     * @param obj - The object being unhovered
     * @param c - The cursor or finger cursor that triggered the event
     */
    private _onUnhover = (obj: Object3D, c: Cursor | FingerCursor) => {
        if (c instanceof FingerCursor) {
            // Ignore, since it's not used
        } else {
            // Handle regular cursor unhover
        }
    };

    /**
     * Handles cursor down events. Triggers the interact method for regular
     * cursors, ignoring FingerCursor events.
     * @param obj - The object being clicked
     * @param c - The cursor or finger cursor that triggered the event
     */
    private _ondown = (obj: Object3D, c: Cursor | FingerCursor) => {
        if (c instanceof FingerCursor) {
            // Ignore, since it's not used
        } else {
            this.interact(c);
        }
    };

    /**
     * Handles cursor up events. Filters out FingerCursor events.
     * @param obj - The object where the cursor was released
     * @param c - The cursor or finger cursor that triggered the event
     */
    private _onup = (obj: Object3D, c: Cursor | FingerCursor) => {
        if (c instanceof FingerCursor) {
            // Ignore, since it's not used
        } else {
            // Handle regular cursor up
        }
    };
}
